import { styled, useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ScooterCounItem from './ScooterCounItem';
import { useSelector } from 'react-redux';
import { selectScooters } from '../../redux/slices/scootersSlice';
import { MapFilters, ScooterStatus } from '../../pages/ZonesPage/interface';
import { getScooterColor } from '../../helpers/scooter-markers-helper';
import ElectricScooterIcon from '@mui/icons-material/ElectricScooter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import BuildIcon from '@mui/icons-material/Build';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Battery20Icon from '@mui/icons-material/Battery20';

const PREFIX = 'ScooterCounters';

const classes = {
  container: `${PREFIX}-container`,
  containerCount: `${PREFIX}-container-count`,
  free: `${PREFIX}-free`,
  unFree: `${PREFIX}-unFree`,
  checked: `${PREFIX}-checked`,
  err: `${PREFIX}-err`
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 8,
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 12
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(5, 1fr)'
    }
  },
  [`& .${classes.containerCount}`]: {
    marginRight: 0,
    marginBottom: 0
  },

  [`& .${classes.free}`]: {
    '& span:nth-child(2)': {
      marginRight: 20,
      fontWeight: 700,
      color: theme.palette.primary.main
    }
  },

  [`& .${classes.unFree}`]: {
    '& span:nth-child(2)': {
      marginRight: 20,
      fontWeight: 700,
      color: theme.palette.error.main
    }
  },

  [`& .${classes.checked}`]: {
    '& span:nth-child(2)': {
      marginRight: 20,
      fontWeight: 700,
      color: theme.palette.info.main
    }
  },
  [`& .${classes.err}`]: {
    '& span:nth-child(2)': {
      marginRight: 20,
      fontWeight: 700,
      color: theme.palette.error.main
    }
  }
}));

interface Props {
  mapFilters?: MapFilters;
  onFilterSet?: (val: MapFilters) => void;
}

const DISABLED_COLOR = '#9B9B9B';

const ScooterCounters = ({ mapFilters, onFilterSet }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    items,
    total,
    free,
    onContract,
    transporting,
    charging,
    maintenance,
    offline,
    unlocked,
    lowBattery
  } = useSelector(selectScooters);

  const broken = useMemo(
    () =>
      items?.filter(scooter => scooter.status === ScooterStatus.BROKEN_IN_CITY)
        ?.length ?? 0,
    [items]
  );

  const onUpdateFilters = (value: MapFilters) => {
    const filters = { ...mapFilters };

    if (
      Object.keys(value).includes('free') ||
      Object.keys(value).includes('offline')
    ) {
      delete filters?.status;
      delete value?.status;
    }

    if (Object.keys(value).includes('status')) {
      delete filters?.free;
      delete value?.free;
      delete filters?.offline;
      delete value?.offline;
    }

    if (value?.free === filters?.free) {
      delete filters?.free;
      delete value?.free;
    }

    if (value?.offline === filters?.offline) {
      delete filters?.offline;
      delete value?.offline;
    }

    const newFilters = {
      ...filters,
      ...value
    };
    onFilterSet?.(newFilters);
  };

  const onSetStatus = (status: ScooterStatus) => {
    let arr = mapFilters?.status?.split(',') ?? [];

    let index = arr.indexOf(status);
    if (index > -1) {
      arr.splice(index, 1);
    } else {
      arr.push(status);
    }

    let newStatus = arr.join(',');

    onUpdateFilters({ status: newStatus });
  };

  const filters = mapFilters ?? {};
  
  const isAll = useMemo(
    () => Object.keys(filters).length === 0,
    [filters]
  );
  const isFree = useMemo(
    () =>
      filters.free ||
      filters.free === 'all',
    [filters]
  );
  const isUnlocked = useMemo(
    () => filters.unlocked,
    [filters]
  );
  const isInRent = useMemo(
    () =>
      (Object.keys(filters).includes('free') &&
        Boolean(filters.free) === false) ||
      filters.free === 'all',
    [filters]
  );
  const isInTransporting = useMemo(
    () => filters?.status?.includes(ScooterStatus.TRANSPORTING),
    [filters]
  );
  const isInCharging = useMemo(
    () => filters?.status?.includes(ScooterStatus.CHARGING),
    [filters]
  );
  const isInMaintainance = useMemo(
    () => filters?.status?.includes(ScooterStatus.MAINTENANCE),
    [filters]
  );
  const isOffline = useMemo(
    () => filters.offline,
    [filters]
  );
  const isBrokenInCity = useMemo(
    () => filters?.status?.includes(ScooterStatus.BROKEN_IN_CITY),
    [filters]
  );

  const isDischarged = useMemo(
    () => filters?.status?.includes(ScooterStatus.LOW_BATTERY),
    [filters]
  );

  return (
    <Root className={classes.container}>
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onFilterSet?.({})}
        title={t('control.all')}
        count={String(total)}
        color={getScooterColor('all')}
        icon={<ElectricScooterIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() =>
          onUpdateFilters(
            isInRent && mapFilters?.free !== 'all'
              ? { free: 'all' }
              : { free: true }
          )
        }
        title={t('control.free')}
        count={String(free)}
        color={getScooterColor('free')}
        icon={<CheckCircleIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() =>
          onUpdateFilters(
            isFree && mapFilters?.free !== 'all'
              ? { free: 'all' }
              : { free: false }
          )
        }
        title={t('control.inRent')}
        count={String(onContract)}
        color={getScooterColor('inRent')}
        icon={<DirectionsBikeIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onUpdateFilters({ unlocked: true })}
        title={t('control.unlocked')}
        count={String(unlocked)}
        color={getScooterColor('unlocked')}
        icon={<LockOpenIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onSetStatus?.(ScooterStatus.TRANSPORTING)}
        title={t('control.transporting')}
        count={String(transporting)}
        color={getScooterColor(ScooterStatus.TRANSPORTING)}
        icon={<LocalShippingIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onSetStatus?.(ScooterStatus.CHARGING)}
        title={t('control.charging')}
        count={String(charging)}
        color={getScooterColor(ScooterStatus.CHARGING)}
        icon={<BatteryChargingFullIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onSetStatus?.(ScooterStatus.MAINTENANCE)}
        title={t('control.maintenance')}
        count={String(maintenance)}
        color={getScooterColor(ScooterStatus.MAINTENANCE)}
        icon={<BuildIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onUpdateFilters({ offline: true })}
        title={t('control.offline')}
        count={String(offline)}
        color={getScooterColor('offline')}
        icon={<WifiOffIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onSetStatus?.(ScooterStatus.BROKEN_IN_CITY)}
        title={t('control.brokenInCity')}
        count={String(broken)}
        color={getScooterColor(ScooterStatus.BROKEN_IN_CITY)}
        icon={<ReportProblemIcon />}
      />
      <ScooterCounItem
        className={classNames(classes.containerCount)}
        onPress={() => onSetStatus?.(ScooterStatus.LOW_BATTERY)}
        title={t('control.discharged')}
        count={String(lowBattery)}
        color={theme.palette.mode === 'light' ? '#000000' : getScooterColor('discharged')}
        icon={<Battery20Icon />}
      />
    </Root>
  );
};

export default ScooterCounters;
